import type { Ref } from 'react';
import {
  TextField as AriaTextField,
  Input,
  TextArea as AriaTextArea,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';
import { cn } from './lib/cn';
import { INPUT_RESET } from './lib/input-reset';

/** A name for screen readers alone, or the id of a visible label. */
type TextFieldName =
  | {
      /** What the field holds, for screen readers. */
      label: string;
      'aria-labelledby'?: never;
    }
  | { 'aria-labelledby': string; label?: never };

export type TextFieldProps = Omit<
  AriaTextFieldProps,
  'className' | 'children' | 'type' | 'aria-label' | 'aria-labelledby'
> &
  TextFieldName & {
    placeholder?: string;
    /** `date` and `number` use the browser's own editors. */
    type?: 'text' | 'number' | 'date';
    /** The `<input>` itself, e.g. for a form library to focus. */
    inputRef?: Ref<HTMLInputElement>;
    /** `sm` inside a table row, `md` everywhere else. */
    size?: 'sm' | 'md';
    className?: string;
  };

/** Single-line input in the house style: hairline pill, no chrome of its own. */
export function TextField({
  label,
  placeholder,
  type = 'text',
  inputRef,
  size = 'md',
  className,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField {...props} aria-label={label} className="sk:inline-flex">
      <Input
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        className={cn(
          INPUT_RESET,
          size === 'sm' ? 'sk:h-6 sk:px-2.5' : 'sk:h-7 sk:px-3',
          'sk:w-full sk:rounded-full sk:text-dense sk:font-medium sk:tracking-dense sk:text-fg',
          'sk:inset-shadow-hairline sk:bg-surface sk:shadow-card sk:outline-none',
          'sk:transition-[box-shadow] sk:duration-75',
          'sk:placeholder:font-normal sk:placeholder:text-fg-tertiary',
          'sk:focus:shadow-none sk:disabled:cursor-not-allowed sk:disabled:opacity-50',
          className,
        )}
      />
    </AriaTextField>
  );
}

export type TextAreaFieldProps = Omit<
  AriaTextFieldProps,
  'className' | 'children'
> & {
  label: string;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  className?: string;
};

export function TextAreaField({
  label,
  placeholder,
  rows = 4,
  maxLength,
  className,
  ...props
}: TextAreaFieldProps) {
  return (
    <AriaTextField {...props} aria-label={label} className="sk:flex sk:w-full">
      <AriaTextArea
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        className={cn(
          INPUT_RESET,
          'sk:min-h-24 sk:w-full sk:resize-y sk:rounded-card sk:p-3',
          'sk:text-dense sk:font-medium sk:tracking-dense sk:text-fg',
          'sk:inset-shadow-hairline sk:bg-surface sk:shadow-card sk:outline-none',
          'sk:placeholder:font-normal sk:placeholder:text-fg-tertiary',
          'sk:focus:shadow-none sk:disabled:cursor-not-allowed sk:disabled:opacity-50',
          className,
        )}
      />
    </AriaTextField>
  );
}
